import type { BatteryArbitragePresentationDto } from "~/api/dto/battery-arbitrage.dto"

import { PeakUsageScenario } from "../PeakUsageScenario"
import { GradText, Section, SectionHeading } from "../primitives"
import { reducesUtilityBill } from "../view-helpers"

const REDUCES_BILL = `Charge at lower rates and discharge during expensive hours. The saved calculation estimates a reduction in utility costs.`
/** Read when the saved run found no bill reduction, so the section cannot promise one. */
const NO_REDUCTION = `On the saved usage and rate assumptions, the calculation found no reduction to claim.`

export function Arbitrage({
  arbitrage,
  interactive = true,
}: {
  arbitrage: BatteryArbitragePresentationDto | null
  interactive?: boolean
}): JSX.Element | null {
  if (!arbitrage?.peakUsageScenario) {
    return null
  }

  const reducesBill = reducesUtilityBill(arbitrage.annualSavingsUsd)
  const scenarioProps = {
    scenario: arbitrage.peakUsageScenario,
    loadShapeSource: arbitrage.assumptions.loadShapeSource,
    measurementBasis: arbitrage.assumptions.measurementBasis,
    estimateBasis: arbitrage.estimateBasis,
    modeledBillingScope: arbitrage.modeledBillingScope,
  }

  return (
    <Section id="savings">
      <SectionHeading
        eyebrow="Smart energy arbitrage"
        sub={reducesBill ? REDUCES_BILL : NO_REDUCTION}
      >
        {reducesBill ? (
          <>
            Buy low, <GradText>use high</GradText>.
          </>
        ) : (
          <>
            What this battery <GradText>actually saves</GradText>.
          </>
        )}
      </SectionHeading>
      {interactive ? (
        <>
          <div className="print:hidden">
            <PeakUsageScenario key={arbitrage.computedAt} {...scenarioProps} />
          </div>
          <div className="hidden print:block">
            <PeakUsageScenario {...scenarioProps} interactive={false} />
          </div>
        </>
      ) : (
        <PeakUsageScenario {...scenarioProps} interactive={false} />
      )}
    </Section>
  )
}
